import React, { useEffect, useState } from "react";
import { Meta } from "../layout/Meta";
import Link from "../components/Link";
import { useAuth, AUTH_PHASES } from "../auth/AuthProvider";
import { allowlistedReturnPath } from "../auth/RequireAuth";
import { formatAuthError } from "../billing/firebaseAuth";
import { isFirebaseConfigured } from "../billing/billingConfig";
import { storePendingConsent } from "../account/consent";
import { navigate } from "../lib/navigate";

function nextPath() {
  const params = new URLSearchParams(window.location.search || "");
  return allowlistedReturnPath(params.get("next"));
}

function AuthShell({ title, description, path, children }) {
  return (
    <>
      <Meta title={title} description={description} path={path} noindex />
      <div className="mx-auto w-full max-w-md px-5 py-16">
        <h1 className="text-3xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted">{description}</p>
        {children}
      </div>
    </>
  );
}

function GoogleButton({ busy, onClick, label }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className="mt-6 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ink px-4 text-sm font-semibold text-white disabled:opacity-60"
    >
      {busy ? "Working…" : label}
    </button>
  );
}

export default function SignInPage() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const next = nextPath();

  useEffect(() => {
    if (auth.phase === AUTH_PHASES.active) {
      navigate(next, { replace: true });
    }
    if (auth.phase === AUTH_PHASES.deletionRequested) {
      navigate("/account/deletion-status", { replace: true });
    }
  }, [auth.phase, next]);

  async function onGoogle() {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn();
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!isFirebaseConfigured()) {
    return (
      <AuthShell
        title="Sign in"
        description="Website sign-in is not configured in this environment."
        path="/sign-in"
      />
    );
  }

  return (
    <AuthShell
      title="Sign in"
      description="Use the same Google account as the Stratify iPhone app. Existing app users keep the same account."
      path="/sign-in"
    >
      {error ? (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      ) : null}
      {auth.phase === AUTH_PHASES.lifecycleUnresolved ? (
        <p role="status" className="mt-4 text-sm text-muted">
          Finishing account setup…
        </p>
      ) : null}
      <GoogleButton busy={busy || auth.phase === AUTH_PHASES.authenticating} onClick={onGoogle} label="Continue with Google" />
      <p className="mt-4 text-sm text-muted">
        New to Stratify?{" "}
        <Link to={`/sign-up?next=${encodeURIComponent(next)}`} className="font-semibold text-ink underline underline-offset-4">
          Create an account
        </Link>
      </p>
      <p className="mt-6 text-xs leading-relaxed text-muted">
        Can’t sign in? Recover access through{" "}
        <a
          href="https://accounts.google.com/signin/recovery"
          className="underline underline-offset-4"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google account recovery
        </a>
        .
      </p>
    </AuthShell>
  );
}

export function SignUpPage() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [noAdvice, setNoAdvice] = useState(false);
  const next = nextPath();
  const canSubmit = terms && privacy && noAdvice;

  useEffect(() => {
    if (auth.phase === AUTH_PHASES.active) {
      navigate(next, { replace: true });
    }
    if (auth.phase === AUTH_PHASES.deletionRequested) {
      navigate("/account/deletion-status", { replace: true });
    }
  }, [auth.phase, next]);

  async function onGoogle() {
    if (!canSubmit) {
      setError("Accept the Terms, Privacy Policy, and research-only notice to continue.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      storePendingConsent();
      await auth.signIn();
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!isFirebaseConfigured()) {
    return (
      <AuthShell
        title="Create your account"
        description="Website sign-in is not configured in this environment."
        path="/sign-up"
      />
    );
  }

  return (
    <AuthShell
      title="Create your account"
      description="Google is the only sign-in method. If you already use Stratify on iPhone with Google, you will get the same account."
      path="/sign-up"
    >
      <fieldset className="mt-6 space-y-3 text-sm text-ink/80">
        <legend className="sr-only">Required agreements</legend>
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>
            I agree to the{" "}
            <Link to="/terms-of-use" className="font-semibold text-ink underline underline-offset-4">
              Terms of Use
            </Link>
            .
          </span>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} />
          <span>
            I agree to the{" "}
            <Link to="/privacy-policy" className="font-semibold text-ink underline underline-offset-4">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={noAdvice} onChange={(e) => setNoAdvice(e.target.checked)} />
          <span>I understand Stratify is for research and simulated paper trading, not financial advice.</span>
        </label>
      </fieldset>
      {error ? (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      ) : null}
      <GoogleButton
        busy={busy || auth.phase === AUTH_PHASES.authenticating}
        onClick={onGoogle}
        label="Continue with Google"
      />
      <p className="mt-4 text-sm text-muted">
        Already have an account?{" "}
        <Link to={`/sign-in?next=${encodeURIComponent(next)}`} className="font-semibold text-ink underline underline-offset-4">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
